import AlertCircleIcon from "@hugeicons/core-free-icons/AlertCircleIcon";
import ArrowLeft01Icon from "@hugeicons/core-free-icons/ArrowLeft01Icon";
import Book02Icon from "@hugeicons/core-free-icons/Book02Icon";
import Compass01Icon from "@hugeicons/core-free-icons/Compass01Icon";
import DashboardSquare01Icon from "@hugeicons/core-free-icons/DashboardSquare01Icon";
import Home01Icon from "@hugeicons/core-free-icons/Home01Icon";
import Refresh01Icon from "@hugeicons/core-free-icons/Refresh01Icon";
import ServerStack01Icon from "@hugeicons/core-free-icons/ServerStack01Icon";
import WifiDisconnected01Icon from "@hugeicons/core-free-icons/WifiDisconnected01Icon";
import { HugeiconsIcon } from "@hugeicons/react";
import {
	Link,
	Outlet,
	useParams,
	useRouterState,
} from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { i18n } from "@/shared/lib/i18n";
import { routes } from "@/shared/lib/routes";
import { APP_ROUTES } from "@/shared/lib/routes/routes/app-routes";
import { useLanguageStore } from "@/shared/lib/store/app-store";
import { BavardWordDecorator } from "@/shared/ui/bavard-word-decorator";
import { Button, buttonVariants } from "@/shared/ui/button";
import { MotionProvider } from "@/shared/ui/motion/index";
import {
	diagnoseRootError,
	getRootErrorDetails,
} from "./root-error-diagnostics";
import { CurrentRouteSkeleton } from "./route-pending-skeleton";

const isDevMode = import.meta.env.MODE === "development";
const INLINE_CODE_TOKEN_PATTERN = /(`[^`]+`)/g;

const AppProviders = lazy(async () => ({
	default: (await import("@/app/providers/index")).AppProviders,
}));

const AcceptInvitationPage = lazy(async () => ({
	default: (await import("@/pages/accept-invitation/index"))
		.AcceptInvitationPage,
}));

function LandingFallback() {
	const isLocalized = useLanguageStore((state) => state.locale !== "en");
	return (
		<output
			aria-busy="true"
			aria-live="polite"
			className="block min-h-screen bg-[var(--brand-paper,#f0f0ff)] px-5 py-5 text-[var(--brand-ink,#1a1a1a)]"
		>
			<span className="sr-only">
				{i18n.getMessage("wgt_router_loading_page", isLocalized)}
			</span>
			<nav className="mx-auto flex max-w-7xl items-center justify-between gap-4">
				<div className="h-9 w-36 rounded-full bg-[hsl(var(--brand-ink-hsl,0_0%_10%)_/_0.1)]" />
				<div className="h-9 w-28 rounded-full bg-[hsl(var(--brand-ink-hsl,0_0%_10%)_/_0.1)]" />
			</nav>
			<section className="mx-auto grid max-w-7xl gap-10 py-16 lg:grid-cols-[minmax(0,1fr)_minmax(22rem,0.8fr)] lg:py-24">
				<div className="space-y-6">
					<div className="h-4 w-40 rounded-full bg-[hsl(var(--brand-ink-hsl,0_0%_10%)_/_0.12)]" />
					<div className="h-16 w-full max-w-2xl rounded-xl bg-[hsl(var(--brand-ink-hsl,0_0%_10%)_/_0.12)]" />
					<div className="h-16 w-4/5 max-w-xl rounded-xl bg-[hsl(var(--brand-ink-hsl,0_0%_10%)_/_0.1)]" />
					<div className="flex flex-wrap gap-3">
						<div className="h-11 w-36 rounded-full bg-[hsl(var(--brand-ink-hsl,0_0%_10%)_/_0.14)]" />
						<div className="h-11 w-32 rounded-full bg-[hsl(var(--brand-ink-hsl,0_0%_10%)_/_0.1)]" />
					</div>
				</div>
				<div className="min-h-80 rounded-3xl border border-[var(--brand-border,#d4d4dc)] bg-[var(--brand-white,#fff)] p-4">
					<div className="h-full min-h-72 w-full rounded-2xl bg-[hsl(var(--brand-ink-hsl,0_0%_10%)_/_0.08)]" />
				</div>
			</section>
		</output>
	);
}

/**
 * Pending state for the `_auth` routes.
 *
 * They used to fall through to the generic app-shell pending state. On /login
 * that is a full-width authenticated frame flashing where a narrow centred form
 * is about to appear — a skeleton for a completely different page.
 *
 * Self-contained by necessity. This renders while `LoginPage` (and the auth
 * feature chunk it pulls) is still in flight, so `.auth-surface` and friends do
 * not exist yet; only brand.css, which ships in globals, is guaranteed loaded.
 * That is also why the old `AuthWidgetSkeleton` — styled with `.auth-column` —
 * could never have worked here.
 *
 * No shimmer: the block is a plain tint. The form has no data to wait on, so
 * this is a module-download gap of a few hundred ms, and an animation drawing
 * attention to it makes the wait feel longer than it is.
 */
function AuthPendingFallback() {
	const isLocalized = useLanguageStore((state) => state.locale !== "en");
	const block = "rounded-[8px] bg-[hsl(var(--brand-hairline-hsl)/0.7)]";
	return (
		<output
			aria-busy="true"
			aria-live="polite"
			className="flex min-h-screen bg-[var(--brand-ground)] px-4 py-8 sm:px-6 sm:py-12 md:py-20"
		>
			<span className="sr-only">
				{i18n.getMessage("wgt_router_loading_page", isLocalized)}
			</span>
			<div className="m-auto w-full max-w-[24rem] p-6 sm:p-7">
				<div className="mb-7 flex items-center justify-between gap-4">
					<span className="bavard-word text-[color:var(--brand-ink-cool)] text-[length:var(--brand-t-wordmark)] leading-none">
						Bavard
					</span>
					<div className={`h-[32px] w-[74px] rounded-full ${block}`} />
				</div>
				{/* Heading + subtitle, start-aligned like the real AuthHeading —
				    20px heading (--brand-t-h3), 14px subtitle. */}
				<div className={`h-5 w-2/5 ${block}`} />
				<div className={`mt-1.5 h-3.5 w-4/5 ${block}`} />
				{/* Two labelled fields, then the CTA — the shape of the real form. */}
				<div className="mt-7 space-y-4">
					<div className="space-y-1.5">
						<div className={`h-3 w-16 ${block}`} />
						<div className={`h-[var(--brand-control-h)] w-full ${block}`} />
					</div>
					<div className="space-y-1.5">
						<div className={`h-3 w-20 ${block}`} />
						<div className={`h-[var(--brand-control-h)] w-full ${block}`} />
					</div>
					<div className={`h-[var(--brand-control-h)] w-full ${block}`} />
				</div>
			</div>
		</output>
	);
}

function PublicLandingLayout() {
	return (
		<MotionProvider>
			<BavardWordDecorator />
			<Suspense fallback={<LandingFallback />}>
				<Outlet />
			</Suspense>
		</MotionProvider>
	);
}

export function RootLayout() {
	const pathname = useRouterState({ select: (s) => s.location.pathname });

	if (pathname === "/") {
		return <PublicLandingLayout />;
	}

	// Same shape as the landing check above. Keyed off the route registry rather
	// than a literal or a hand-kept list of the `_auth` group, so there is no
	// second copy of the path to drift. Scoped to /login deliberately: the other
	// `_auth` routes (accept-invitation, pending-org, onboarding) are not
	// form-column pages, so a group-wide auth skeleton would be as wrong for
	// them as the app-shell one is here.
	// Every other route gets the app's OWN layout while it loads: navbar, rail
	// gutter, and a skeleton shaped like the page that is arriving. It used to
	// get a generic centred three-card box with no chrome at all, so a refresh
	// drew one screen and then replaced it wholesale with a structurally
	// different one — the flash.
	//
	// `CurrentRouteSkeleton` was written for exactly this and then never wired
	// up; the startup-boundary guard already keeps it free of the page, feature
	// and widget graphs, which is what makes it safe on this path.
	const fallback =
		pathname === routes.login ? (
			<AuthPendingFallback />
		) : (
			<CurrentRouteSkeleton />
		);

	// Both boundaries below use the same choice, so the pending UI never changes
	// shape mid-load — the outer one covers the AppProviders chunk on a cold
	// load, the inner one the page chunk after it.
	return (
		<Suspense fallback={fallback}>
			{/* AppProviders is mounted INSIDE the router so providers that use
			    TanStack Router hooks (AuthGate, TourMounter, CommandPaletteMount)
			    have a RouterProvider parent. It is lazy-loaded so the public
			    landing route does not pull the authenticated app shell into the
			    first paint path. Keep route-level suspension INSIDE AppProviders:
			    unmounting the session/auth/query providers for a lazy page or
			    same-route search transition can restart their requests and strand
			    the whole authenticated app on this fallback. */}
			<AppProviders>
				<BavardWordDecorator />
				<Suspense fallback={fallback}>
					<Outlet />
				</Suspense>
			</AppProviders>
		</Suspense>
	);
}

const NOT_FOUND_RESOURCES = [
	{
		to: APP_ROUTES.home,
		icon: Home01Icon,
		labelKey: "not_found_resource_home_label",
		descriptionKey: "not_found_resource_home_description",
	},
	{
		to: "/dashboard",
		icon: DashboardSquare01Icon,
		labelKey: "not_found_resource_dashboard_label",
		descriptionKey: "not_found_resource_dashboard_description",
	},
	{
		to: "/docs",
		icon: Book02Icon,
		labelKey: "not_found_resource_docs_label",
		descriptionKey: "not_found_resource_docs_description",
	},
] as const;

export function NotFound() {
	const isLocalized = useLanguageStore((state) => state.locale !== "en");
	return (
		<main className="flex min-h-screen items-center justify-center bg-background px-4 py-8 text-foreground sm:px-6">
			<section
				aria-labelledby="not-found-title"
				className="w-full max-w-xl rounded-lg border bg-card p-5 shadow-sm sm:p-6"
			>
				<div className="flex flex-col gap-4 sm:flex-row sm:items-start">
					<div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
						<HugeiconsIcon
							aria-hidden="true"
							className="size-6"
							icon={Compass01Icon}
						/>
					</div>
					<div className="min-w-0 flex-1">
						<p className="font-medium text-primary text-sm">
							{i18n.getMessage("not_found_status", isLocalized)}
						</p>
						<h1
							className="mt-1 text-balance font-semibold text-2xl tracking-normal"
							id="not-found-title"
						>
							{i18n.getMessage("not_found_title", isLocalized)}
						</h1>
						<p className="mt-3 max-w-md text-muted-foreground text-sm leading-6">
							{i18n.getMessage("not_found_description", isLocalized)}
						</p>
					</div>
				</div>

				<nav
					aria-label={i18n.getMessage("not_found_title", isLocalized)}
					className="mt-6 grid gap-1 rounded-lg border bg-surface-well p-2"
				>
					{NOT_FOUND_RESOURCES.map((resource) => (
						<Link
							className="group flex items-center gap-3 rounded-md p-3 text-left outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring"
							key={resource.to}
							to={resource.to}
						>
							<span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-card text-primary shadow-sm ring-1 ring-border/60">
								<HugeiconsIcon
									aria-hidden="true"
									className="size-4"
									icon={resource.icon}
								/>
							</span>
							<span className="min-w-0 flex-1">
								<span className="block font-medium text-sm">
									{i18n.getMessage(resource.labelKey, isLocalized)}
								</span>
								<span className="block truncate text-muted-foreground text-xs leading-5 group-hover:text-accent-foreground/80">
									{i18n.getMessage(resource.descriptionKey, isLocalized)}
								</span>
							</span>
							<HugeiconsIcon
								aria-hidden="true"
								className="size-4 shrink-0 rotate-180 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-accent-foreground"
								icon={ArrowLeft01Icon}
							/>
						</Link>
					))}
				</nav>

				<div className="mt-6 flex flex-col gap-3 sm:flex-row">
					<button
						className={buttonVariants({
							className: "w-full sm:w-auto",
							variant: "outline",
						})}
						onClick={() => window.history.back()}
						type="button"
					>
						<HugeiconsIcon
							aria-hidden="true"
							className="size-4"
							icon={ArrowLeft01Icon}
						/>
						{i18n.getMessage("not_found_go_back", isLocalized)}
					</button>
					<Link
						className={buttonVariants({ className: "w-full sm:w-auto" })}
						to={APP_ROUTES.home}
					>
						<HugeiconsIcon
							aria-hidden="true"
							className="size-4"
							icon={Home01Icon}
						/>
						{i18n.getMessage("not_found_take_me_home", isLocalized)}
					</Link>
				</div>
			</section>
		</main>
	);
}

function RootErrorCheckText({ text }: { text: string }) {
	return text.split(INLINE_CODE_TOKEN_PATTERN).map((part) => {
		if (part.startsWith("`") && part.endsWith("`")) {
			return (
				<code
					className="rounded bg-muted px-1 py-0.5 font-mono text-foreground text-xs"
					key={part}
				>
					{part.slice(1, -1)}
				</code>
			);
		}

		return part;
	});
}

export function RootError({ error }: { error: Error }) {
	const isLocalized = useLanguageStore((state) => state.locale !== "en");
	const diagnosis = diagnoseRootError(error, isLocalized);
	const detailText = getRootErrorDetails(error, isLocalized);
	let Icon: typeof ServerStack01Icon;
	let statusLabel: string;
	if (diagnosis.kind === "frontend-module") {
		Icon = ServerStack01Icon;
		statusLabel = i18n.getMessage("wgt_root_error_status_module", isLocalized);
	} else if (diagnosis.kind === "backend-connection") {
		Icon = WifiDisconnected01Icon;
		statusLabel = i18n.getMessage("wgt_root_error_status_backend", isLocalized);
	} else {
		Icon = AlertCircleIcon;
		statusLabel = i18n.getMessage("wgt_root_error_status_unknown", isLocalized);
	}
	const checks = isDevMode
		? [...diagnosis.checks, ...diagnosis.devChecks]
		: diagnosis.checks;

	return (
		<main className="flex min-h-screen items-center justify-center bg-background px-4 py-8 text-foreground sm:px-6">
			<section
				aria-labelledby="root-error-title"
				className="w-full max-w-3xl rounded-lg border bg-card p-5 shadow-sm sm:p-6"
				role="alert"
			>
				<div className="flex flex-col gap-4 sm:flex-row sm:items-start">
					<div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
						<HugeiconsIcon aria-hidden="true" className="size-6" icon={Icon} />
					</div>
					<div className="min-w-0 flex-1">
						<p className="font-medium text-destructive text-sm">
							{statusLabel}
						</p>
						<h1
							className="mt-1 text-balance font-semibold text-2xl tracking-normal"
							id="root-error-title"
						>
							{diagnosis.title}
						</h1>
						<p className="mt-3 max-w-2xl text-muted-foreground text-sm leading-6 sm:text-base">
							{diagnosis.description}
						</p>
					</div>
				</div>

				<div className="mt-6 rounded-lg border bg-surface-well p-4">
					<h2 className="font-medium text-sm">
						{i18n.getMessage("wgt_root_error_what_to_check", isLocalized)}
					</h2>
					<ul className="mt-3 grid gap-2 text-muted-foreground text-sm">
						{checks.map((check) => (
							<li className="flex gap-2" key={check}>
								<span
									aria-hidden="true"
									className="mt-2 size-1.5 rounded-full bg-primary"
								/>
								<span className="min-w-0 leading-6">
									<RootErrorCheckText text={check} />
								</span>
							</li>
						))}
					</ul>
				</div>

				<div className="mt-6 flex flex-col gap-3 sm:flex-row">
					<Button
						className="w-full sm:w-auto"
						onClick={() => window.location.reload()}
						type="button"
					>
						<HugeiconsIcon
							aria-hidden="true"
							className="size-4"
							icon={Refresh01Icon}
						/>
						{i18n.getMessage("button_refresh", isLocalized)}
					</Button>
					<a
						className={buttonVariants({
							className: "w-full sm:w-auto",
							variant: "outline",
						})}
						href={APP_ROUTES.home}
					>
						<HugeiconsIcon
							aria-hidden="true"
							className="size-4"
							icon={Home01Icon}
						/>
						{i18n.getMessage("wgt_root_error_open_start_page", isLocalized)}
					</a>
				</div>

				<details className="mt-6 rounded-lg border bg-muted/30 p-4">
					<summary className="cursor-pointer font-medium text-sm">
						{i18n.getMessage("wgt_root_error_technical_details", isLocalized)}
					</summary>
					<pre className="mt-3 max-h-56 overflow-auto whitespace-pre-wrap break-words text-muted-foreground text-xs leading-5">
						{detailText}
					</pre>
				</details>
			</section>
		</main>
	);
}

export function LayoutOutlet() {
	return <Outlet />;
}

export function AcceptInvitationRoute() {
	const { id } = useParams({ from: "/accept-invitation/$id" });
	return <AcceptInvitationPage invitationId={id} />;
}
